import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import Header from "../components/Header";
import { Search, MoreVertical, Edit2, Trash2, Eye, X, Plus, Download, ArrowUpDown, ChevronUp, ChevronDown, Upload, UserCheck, UserX, MapPin } from "lucide-react";
import Swal from "sweetalert2";
import * as XLSX from "xlsx";
import { API_BASE_URL } from "../config";
import { searchCities, POPULAR_CITIES } from "../utils/indianCities";

export default function Users() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingUserId, setEditingUserId] = useState(null);
  const initialFormData = {
    mobile_number: "",
    country_code: "+91",
    fullName: "",
    email: "",
    designation: "",
    date_of_birth: "",
    gender: "Male",
    city: "",
    business_name: "",
    businessName: "",
    business_email: "",
    business_phone: "",
    business_address: "",
    business_description: "",
    business_type: "",
    content_preference: "Business",
    status: "Free",
    user_group: "",
    businessLogo: ""
  };
  const [formData, setFormData] = useState(initialFormData);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);

  // Close dropdown when clicking outside
  const dropdownRef = useRef(null);
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpenDropdownId(null);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const [users, setUsers] = useState([]);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [bulkUpdatingStatus, setBulkUpdatingStatus] = useState(false);
  const [loading, setLoading] = useState(true);
  const [preferenceOptions, setPreferenceOptions] = useState([]);
  const [businessTypeOptions, setBusinessTypeOptions] = useState([]);
  const [userGroups, setUserGroups] = useState([]);

  // City Autocomplete States
  const [citySuggestions, setCitySuggestions] = useState([]);
  const [showCitySuggestions, setShowCitySuggestions] = useState(false);
  const [activeCityIndex, setActiveCityIndex] = useState(-1);
  const cityInputRef = useRef(null);
  const cityDropdownRef = useRef(null);

  // Close city dropdown on outside click
  useEffect(() => {
    function handleClickOutsideCity(event) {
      if (
        cityDropdownRef.current &&
        !cityDropdownRef.current.contains(event.target) &&
        cityInputRef.current &&
        !cityInputRef.current.contains(event.target)
      ) {
        setShowCitySuggestions(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutsideCity);
    return () => document.removeEventListener("mousedown", handleClickOutsideCity);
  }, []);

  // Extract unique cities from existing database users to merge with standard list
  const existingUserCities = React.useMemo(() => {
    const set = new Set();
    users.forEach(u => {
      if (u && u.city && typeof u.city === 'string') {
        const c = u.city.trim();
        if (c) set.add(c);
      }
    });
    return Array.from(set);
  }, [users]);

  const handleCityChange = (val) => {
    setFormData(prev => ({ ...prev, city: val }));
    if (val && val.trim().length > 0) {
      const matches = searchCities(val, existingUserCities, 12);
      setCitySuggestions(matches);
      setShowCitySuggestions(matches.length > 0);
      setActiveCityIndex(-1);
    } else {
      setCitySuggestions([]);
      setShowCitySuggestions(false);
    }
  };

  const handleSelectCity = (cityName) => {
    setFormData(prev => ({ ...prev, city: cityName }));
    setShowCitySuggestions(false);
    setActiveCityIndex(-1);
  };

  const handleCityKeyDown = (e) => {
    if (!showCitySuggestions || citySuggestions.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveCityIndex(prev => (prev < citySuggestions.length - 1 ? prev + 1 : 0));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveCityIndex(prev => (prev > 0 ? prev - 1 : citySuggestions.length - 1));
    } else if (e.key === "Enter") {
      if (activeCityIndex >= 0 && activeCityIndex < citySuggestions.length) {
        e.preventDefault();
        handleSelectCity(citySuggestions[activeCityIndex]);
      }
    } else if (e.key === "Escape") {
      setShowCitySuggestions(false);
    }
  };

  useEffect(() => {
    fetch(`${API_BASE_URL}/api/groups/all`)
      .then(res => res.json())
      .then(result => setUserGroups(result.status && result.data ? result.data : []))
      .catch(err => console.error("Error fetching groups:", err));
  }, []);

  useEffect(() => {
    if (formData.content_preference === 'Business' || formData.content_preference === 'Politics') {
      const endpoint = formData.content_preference === 'Business' ? '/api/business-names/all' : '/api/politics-names/all';
      fetch(`${API_BASE_URL}${endpoint}`)
        .then(res => res.json())
        .then(result => setPreferenceOptions(result.status && result.data ? result.data : []))
        .catch(err => setPreferenceOptions([]));

      const typeEndpoint = formData.content_preference === 'Business' ? '/api/categories/all-categories' : '/api/politics/all-politics';
      fetch(`${API_BASE_URL}${typeEndpoint}`)
        .then(res => res.json())
        .then(result => setBusinessTypeOptions(result.status && result.data ? result.data : []))
        .catch(err => setBusinessTypeOptions([]));
    } else {
      setPreferenceOptions([]);
      setBusinessTypeOptions([]);
    }
  }, [formData.content_preference]);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10; // Adjust items per page if necessary

  // Reset to first page when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const fetchUsers = () => {
    setLoading(true);
    fetch(`${API_BASE_URL}/api/users/all-users`)
      .then((res) => {
        if (!res.ok) throw new Error("Failed to fetch users");
        return res.json();
      })
      .then((resJson) => {
        if (resJson && resJson.status && resJson.data) {
          const mapped = resJson.data.map((user) => ({
            id: user.user_id_PK,
            userimage: user.userimage || "https://i.pravatar.cc/150",
            mobile_number: user.mobile_number || "",
            country_code: user.country_code || "+91",
            fullName: user.fullname || "",
            email: user.email || "",
            designation: user.designation || "",
            date_of_birth: user.date_of_birth ? new Date(user.date_of_birth).toISOString().split('T')[0] : "",
            gender: user.gender || "Male",
            city: user.city || "",
            business_name: user.business_name || "",
            businessName: user.business_name || "",
            business_email: user.business_email || "",
            business_phone: user.business_phone || "",
            business_address: user.business_address || "",
            business_description: user.business_description || "",
            business_type: user.business_type || "",
            content_preference: String(user.content_preference) === '1' ? 'Business' : (String(user.content_preference) === '2' ? 'Politics' : (user.content_preference || 'Business')),
            businessLogo: user.business_logo || "https://i.pravatar.cc/150",
            frames: user.frames || "",
            is_verified: user.is_verified !== undefined && user.is_verified !== null ? Number(user.is_verified) : 1,
            status: Number(user.is_verified) === 0 ? "Premium" : "Free",
            user_group: user.user_group || "",
            is_active: user.is_active === undefined ? 1 : user.is_active,
            templatesShared: 0,
            bgRemovalsUsed: 0,
            joinDate: user.created_at
              ? new Date(user.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
              : "N/A"
          }));
          setUsers(mapped);
        } else {
          throw new Error(resJson.message || "Invalid response format");
        }
        setLoading(false);
      })
      .catch((err) => {
        console.error("Users fetch error:", err);
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchUsers();
  }, []);


  const handleSaveUser = async () => {
    if (!formData.mobile_number || !formData.fullName) {
      Swal.fire("Error", "Mobile Number and Full Name are required!", "error");
      return;
    }

    try {
      const submitData = new FormData();
      if (editingUserId) submitData.append('id', editingUserId);
      submitData.append('mobile_number', formData.mobile_number);
      submitData.append('country_code', formData.country_code);
      submitData.append('fullName', formData.fullName);
      submitData.append('email', formData.email || "");
      submitData.append('designation', formData.designation || "");
      submitData.append('date_of_birth', formData.date_of_birth || "");
      submitData.append('gender', formData.gender || "Male");
      submitData.append('city', formData.city || "");
      const finalBizName = formData.business_name || formData.businessName || "";
      submitData.append('business_name', finalBizName);
      submitData.append('businessName', finalBizName);
      submitData.append('business_email', formData.business_email || "");
      submitData.append('business_phone', formData.business_phone || "");
      submitData.append('business_address', formData.business_address || "");
      submitData.append('business_description', formData.business_description || "");
      submitData.append('business_type', formData.business_type || "");
      submitData.append('businessType', formData.business_type || "");
      submitData.append('content_preference', formData.content_preference || "Business");
      submitData.append('status', formData.status || "Free");
      submitData.append('user_group', formData.user_group || "");

      if (logoFile) {
        submitData.append('business_logo', logoFile);
      }

      if (editingUserId) {
        const res = await fetch(`${API_BASE_URL}/api/users/admin-update-user`, {
          method: 'POST',
          body: submitData
        });
        const data = await res.json();

        if (data.status) {
          Swal.fire("Success", "User updated successfully!", "success");
          fetchUsers();
          setFormData(initialFormData);
          setLogoFile(null);
          setLogoPreview(null);
          setEditingUserId(null);
          setIsModalOpen(false);
        } else {
          Swal.fire("Error", data.message || "Failed to update user", "error");
        }
      } else {
        const res = await fetch(`${API_BASE_URL}/api/users/admin-create-user`, {
          method: 'POST',
          body: submitData
        });
        const data = await res.json();

        if (data.status && data.data) {
          Swal.fire("Success", "User created successfully!", "success");
          fetchUsers();
          setFormData(initialFormData);
          setLogoFile(null);
          setLogoPreview(null);
          setIsModalOpen(false);
        } else {
          Swal.fire("Error", data.message || "Failed to create user", "error");
        }
      }
    } catch (error) {
      console.error(error);
      Swal.fire("Error", "Something went wrong sending data to server", "error");
    }
  };

  const handleDownloadExcel = () => {
    if (users.length === 0) {
      Swal.fire("Error", "No user data available to download.", "error");
      return;
    }

    const dataToExport = users.map(u => ({
      ID: u.id,
      "Full Name": u.fullName,
      "Mobile Number": u.country_code + " " + u.mobile_number,
      Email: u.email,
      "Date of Birth": u.date_of_birth,
      Gender: u.gender,
      City: u.city,
      Designation: u.designation,
      "Business Name": u.businessName,
      "Business Email": u.business_email,
      "Business Phone": u.business_phone,
      "Business Type": u.business_type,
      "Content Preference": u.content_preference,
      Status: u.status,
      "is_verified": u.is_verified,
      "User Group": u.user_group || 'None',
      "Join Date": u.joinDate
    }));

    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Users");

    XLSX.writeFile(workbook, "Users_Data.xlsx");
  };

  const handleDeleteUser = (id) => {
    setOpenDropdownId(null);
    Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const res = await fetch(`${API_BASE_URL}/api/users/user/${id}`, {
            method: 'DELETE'
          });
          const resJson = await res.json();
          if (resJson.status) {
            setUsers((prev) => prev.filter((u) => u.id !== id));
            setSelectedUserIds((prev) => prev.filter((userId) => userId !== id));
            Swal.fire('Deleted!', 'User has been deleted.', 'success');
          } else {
            Swal.fire('Error', resJson.message || 'Failed to delete user', 'error');
          }
        } catch (err) {
          console.error("Delete user error:", err);
          Swal.fire('Error', 'Failed to delete user', 'error');
        }
      }
    });
  };

  const handleToggleActive = (id, currentStatus) => {
    const newStatus = currentStatus ? 0 : 1;
    fetch(`${API_BASE_URL}/api/users/toggle-active-status`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ user_id: id, is_active: newStatus }),
    })
      .then((res) => res.json())
      .then((resJson) => {
        if (resJson.status) {
          setUsers(users.map((u) => (u.id === id ? { ...u, is_active: newStatus } : u)));
          Swal.fire({
            icon: "success",
            title: "Success",
            text: resJson.message,
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 3000
          });
        } else {
          Swal.fire("Error", resJson.message, "error");
        }
      })
      .catch(() => Swal.fire("Error", "Failed to update status", "error"));
  };

  const getStatusBadge = (status, isVerified) => {
    const isPrem = status === 'Premium' || isVerified === 0;
    if (isPrem) return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-amber-50 dark:bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-200/60 dark:border-amber-500/20">
        <span className="w-1.5 h-1.5 bg-amber-500 rounded-full"></span>
        Premium
      </span>
    );
    if (status === 'Free' || isVerified === 1) return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-indigo-50 dark:bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 border border-indigo-200/60 dark:border-indigo-500/20">
        <span className="w-1.5 h-1.5 bg-indigo-500 rounded-full"></span>
        Free
      </span>
    );
    return <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-rose-50 dark:bg-rose-500/10 text-rose-600 dark:text-rose-400"><span className="w-1.5 h-1.5 bg-rose-500 rounded-full"></span>Blocked</span>;
  };

  const filteredUsers = users.filter(u =>
    u.fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.businessName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (u.mobile_number && u.mobile_number.includes(searchTerm)) ||
    (u.country_code && (u.country_code + u.mobile_number).includes(searchTerm.replace(/\s+/g, '')))
  );

  // Sorting states
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'ascending' });

  const requestSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return <ArrowUpDown className="w-3.5 h-3.5 inline ml-1 opacity-40 group-hover:opacity-100 transition-opacity" />;
    if (sortConfig.direction === 'ascending') return <ChevronUp className="w-3.5 h-3.5 inline ml-1 text-blue-500" />;
    return <ChevronDown className="w-3.5 h-3.5 inline ml-1 text-blue-500" />;
  };

  const sortedUsers = React.useMemo(() => {
    let sortableUsers = [...filteredUsers];
    if (sortConfig.key !== null) {
      sortableUsers.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        // Handle undefined or null
        if (aValue === undefined || aValue === null) aValue = '';
        if (bValue === undefined || bValue === null) bValue = '';

        // Convert dates to timestamps if comparing dates
        if (sortConfig.key === 'joinDate') {
          aValue = new Date(aValue).getTime();
          bValue = new Date(bValue).getTime();
        } else if (typeof aValue === 'string') {
          aValue = aValue.toLowerCase();
          bValue = bValue.toLowerCase();
        }

        if (aValue < bValue) return sortConfig.direction === 'ascending' ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === 'ascending' ? 1 : -1;
        return 0;
      });
    }
    return sortableUsers;
  }, [filteredUsers, sortConfig]);

  const totalPages = Math.ceil(sortedUsers.length / itemsPerPage);
  const paginatedUsers = sortedUsers.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const isAllSelected = sortedUsers.length > 0 && sortedUsers.every((u) => selectedUserIds.includes(u.id));
  const isSomeSelected = selectedUserIds.length > 0 && !isAllSelected;

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const allIds = sortedUsers.map((u) => u.id);
      setSelectedUserIds(allIds);
    } else {
      setSelectedUserIds([]);
    }
  };

  const handleSelectUser = (id) => {
    setSelectedUserIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const handleBulkToggleActive = (targetStatus) => {
    if (!selectedUserIds || selectedUserIds.length === 0) return;

    const actionText = targetStatus === 1 ? 'Active' : 'Inactive';
    const confirmBtnColor = targetStatus === 1 ? '#059669' : '#d97706';

    Swal.fire({
      title: `Mark ${selectedUserIds.length} user${selectedUserIds.length > 1 ? 's' : ''} as ${actionText}?`,
      text: `Selected users will be updated to ${actionText}.`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: confirmBtnColor,
      cancelButtonColor: '#64748b',
      confirmButtonText: `Yes, mark as ${actionText}!`,
      cancelButtonText: 'Cancel'
    }).then(async (result) => {
      if (result.isConfirmed) {
        setBulkUpdatingStatus(true);
        try {
          const res = await fetch(`${API_BASE_URL}/api/users/bulk-toggle-active-status`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ user_ids: selectedUserIds, is_active: targetStatus })
          });
          const resJson = await res.json();
          if (resJson.status) {
            setUsers((prev) =>
              prev.map((u) =>
                selectedUserIds.includes(u.id) ? { ...u, is_active: targetStatus } : u
              )
            );
            Swal.fire({
              icon: 'success',
              title: 'Updated!',
              text: resJson.message || `Users marked as ${actionText} successfully.`,
              timer: 2500,
              showConfirmButton: false,
              toast: true,
              position: 'top-end'
            });
            setSelectedUserIds([]);
          } else {
            Swal.fire('Error', resJson.message || 'Failed to update user status', 'error');
          }
        } catch (err) {
          console.error("Bulk toggle active error:", err);
          Swal.fire('Error', 'Failed to update user status', 'error');
        } finally {
          setBulkUpdatingStatus(false);
        }
      }
    });
  };

  const handleBulkDelete = () => {
    if (!selectedUserIds || selectedUserIds.length === 0) return;

    Swal.fire({
      title: `Delete ${selectedUserIds.length} user${selectedUserIds.length > 1 ? 's' : ''}?`,
      text: "You won't be able to revert this! Selected user accounts and their associated data will be deleted.",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: `Yes, delete ${selectedUserIds.length > 1 ? 'them' : 'it'}!`,
      cancelButtonText: 'Cancel'
    }).then(async (result) => {
      if (result.isConfirmed) {
        setBulkDeleting(true);
        try {
          const res = await fetch(`${API_BASE_URL}/api/users/bulk-delete`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ user_ids: selectedUserIds })
          });
          const resJson = await res.json();
          if (resJson.status) {
            setUsers((prev) => prev.filter((u) => !selectedUserIds.includes(u.id)));
            setSelectedUserIds([]);
            Swal.fire({
              icon: 'success',
              title: 'Deleted!',
              text: resJson.message || 'Users deleted successfully.',
              timer: 2000,
              showConfirmButton: false
            });
          } else {
            Swal.fire('Error', resJson.message || 'Failed to delete users', 'error');
          }
        } catch (err) {
          console.error("Bulk delete error:", err);
          Swal.fire('Error', 'Failed to delete users', 'error');
        } finally {
          setBulkDeleting(false);
        }
      }
    });
  };

  return (
    <div className="flex bg-[#f1f5f9] dark:bg-slate-950 min-h-screen text-slate-800 dark:text-slate-100 font-sans">

      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />

      <div className="flex-1 lg:ml-64 flex flex-col min-h-screen overflow-y-auto">
        <Header
          onMenuClick={() => setIsSidebarOpen(true)}
          title="Users Management"
          subtitle="Manage app users, their business profiles, and track feature usage."
        />

        <div className="p-4 sm:p-6 lg:p-8 flex-1 flex flex-col w-full pb-24">

          {/* Table Card */}
          <div className="bg-white dark:bg-slate-900 shadow-sm rounded-2xl border border-slate-200 dark:border-slate-800 overflow-hidden flex flex-col flex-1">

            {/* Toolbar */}
            <div className="px-5 py-4 border-b border-slate-100 dark:border-slate-800 flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="relative w-full sm:w-80">
                <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  placeholder="Search users or businesses..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-slate-200 border border-slate-200 dark:border-slate-700 rounded-xl pl-9 pr-4 py-2.5 text-sm outline-none focus:border-indigo-400 focus:ring-4 focus:ring-indigo-400/10 transition-all placeholder-slate-400"
                />
              </div>

              {/* Legend */}
              <div className="hidden lg:flex items-center gap-4 text-xs font-medium bg-slate-50 dark:bg-slate-800 px-4 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700">
                <div className="flex items-center gap-1.5 text-slate-600 dark:text-slate-300">
                  <div className="w-2.5 h-2.5 rounded-full bg-red-500"></div> Inactive
                </div>
                <div className="flex items-center gap-1.5 text-slate-600 dark:text-slate-300">
                  <div className="w-2.5 h-2.5 rounded-full bg-amber-300"></div> No Frames
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
                <select className="bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm text-slate-600 dark:text-slate-300 rounded-xl px-3 py-2.5 outline-none focus:border-indigo-400 cursor-pointer flex-1 sm:flex-none">
                  <option>All Status</option>
                  <option>Premium</option>
                  <option>Free</option>
                </select>
                <button
                  onClick={handleDownloadExcel}
                  className="flex flex-1 sm:flex-none items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-semibold rounded-xl px-4 py-2.5 shadow-sm transition-all"
                >
                  <Download size={15} /> Export
                </button>
                <button
                  onClick={() => {
                    setFormData(initialFormData);
                    setLogoFile(null);
                    setLogoPreview(null);
                    setEditingUserId(null);
                    setCitySuggestions([]);
                    setShowCitySuggestions(false);
                    setIsModalOpen(true);
                  }}
                  className="flex flex-1 sm:flex-none items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold rounded-xl px-4 py-2.5 shadow-sm transition-all"
                >
                  <Plus size={15} /> Add User
                </button>
              </div>
            </div>

            {/* Selection Banner */}
            {selectedUserIds.length > 0 && (
              <div className="px-5 py-3 bg-indigo-50/80 dark:bg-slate-800/80 border-b border-indigo-100 dark:border-slate-700/80 flex flex-wrap items-center justify-between gap-3 transition-all">
                <div className="flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-indigo-600 text-white text-xs font-bold flex items-center justify-center">
                    {selectedUserIds.length}
                  </span>
                  <span className="text-sm font-semibold text-slate-800 dark:text-slate-200">
                    {selectedUserIds.length} user{selectedUserIds.length > 1 ? 's' : ''} selected
                  </span>
                  <button
                    type="button"
                    onClick={() => setSelectedUserIds([])}
                    className="ml-2 text-xs font-medium text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-white underline transition-colors"
                  >
                    Deselect All
                  </button>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={() => handleBulkToggleActive(1)}
                    disabled={bulkUpdatingStatus}
                    className="flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold px-3 py-1.5 rounded-lg shadow-sm transition-all disabled:opacity-50"
                  >
                    <UserCheck size={14} /> Mark Active ({selectedUserIds.length})
                  </button>
                  <button
                    type="button"
                    onClick={() => handleBulkToggleActive(0)}
                    disabled={bulkUpdatingStatus}
                    className="flex items-center gap-1.5 bg-amber-600 hover:bg-amber-700 text-white text-xs font-semibold px-3 py-1.5 rounded-lg shadow-sm transition-all disabled:opacity-50"
                  >
                    <UserX size={14} /> Mark Inactive ({selectedUserIds.length})
                  </button>
                  <button
                    type="button"
                    onClick={handleBulkDelete}
                    disabled={bulkDeleting}
                    className="flex items-center gap-1.5 bg-red-600 hover:bg-red-700 text-white text-xs font-semibold px-3 py-1.5 rounded-lg shadow-sm transition-all disabled:opacity-50"
                  >
                    <Trash2 size={14} /> Delete ({selectedUserIds.length})
                  </button>
                </div>
              </div>
            )}

            {/* Table Container */}
            <div className="overflow-x-auto min-h-[240px]">
              <table className="w-full text-left border-collapse whitespace-nowrap min-w-max">
                <thead>
                  <tr className="bg-slate-50 dark:bg-slate-800/60">
                    <th className="px-5 py-3.5 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider sticky left-0 bg-slate-50 dark:bg-slate-800/60 z-20 select-none">
                      <div className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          checked={isAllSelected}
                          ref={(el) => {
                            if (el) el.indeterminate = isSomeSelected;
                          }}
                          onChange={handleSelectAll}
                          disabled={sortedUsers.length === 0}
                          className="w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer disabled:opacity-40"
                          title={isAllSelected ? "Deselect All" : "Select All"}
                        />
                        <span
                          onClick={() => requestSort('fullName')}
                          className="cursor-pointer group flex items-center gap-1 hover:text-slate-700 dark:hover:text-slate-200"
                        >
                          User Info {getSortIcon('fullName')}
                        </span>
                      </div>
                    </th>
                    <th onClick={() => requestSort('businessName')} className="px-5 py-3.5 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider cursor-pointer select-none group hover:text-slate-700 dark:hover:text-slate-200">
                      Business {getSortIcon('businessName')}
                    </th>
                    <th onClick={() => requestSort('content_preference')} className="px-5 py-3.5 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider cursor-pointer select-none group hover:text-slate-700 dark:hover:text-slate-200">
                      Type {getSortIcon('content_preference')}
                    </th>
                    <th onClick={() => requestSort('status')} className="px-5 py-3.5 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider cursor-pointer select-none group hover:text-slate-700 dark:hover:text-slate-200">
                      Plan {getSortIcon('status')}
                    </th>
                    <th onClick={() => requestSort('user_group')} className="px-5 py-3.5 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider cursor-pointer select-none group hover:text-slate-700 dark:hover:text-slate-200">
                      Group {getSortIcon('user_group')}
                    </th>
                    <th onClick={() => requestSort('joinDate')} className="px-5 py-3.5 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider cursor-pointer select-none group hover:text-slate-700 dark:hover:text-slate-200">
                      Joined {getSortIcon('joinDate')}
                    </th>
                    <th onClick={() => requestSort('is_active')} className="px-5 py-3.5 text-center text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider cursor-pointer select-none group hover:text-slate-700 dark:hover:text-slate-200">
                      Active {getSortIcon('is_active')}
                    </th>
                    <th className="px-5 py-3.5 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider text-right sticky right-0 bg-slate-50 dark:bg-slate-800/60 z-10">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {loading ? (
                    <tr>
                      <td colSpan="8" className="p-10 text-center">
                        <div className="flex items-center justify-center gap-3 text-slate-500 dark:text-slate-400">
                          <div className="w-5 h-5 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin"></div>
                          Loading users...
                        </div>
                      </td>
                    </tr>
                  ) : sortedUsers.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="p-10 text-center text-slate-400 dark:text-slate-500">
                        No users found
                      </td>
                    </tr>
                  ) : (
                    sortedUsers.map((user, index) => {
                      const hasFrames = user.frames && user.frames.length > 0;
                      const isInactive = user.is_active === 0;
                      const isSelected = selectedUserIds.includes(user.id);

                      let rowBg = 'hover:bg-slate-50 dark:hover:bg-slate-800/50';
                      let stickyCellBg = 'bg-white dark:bg-slate-900 group-hover:bg-slate-50 dark:group-hover:bg-slate-800/50';

                      if (isSelected) {
                        rowBg = 'bg-indigo-50/70 hover:bg-indigo-100/70 dark:bg-indigo-950/40 dark:hover:bg-indigo-950/50';
                        stickyCellBg = 'bg-indigo-50/70 dark:bg-indigo-950/40 group-hover:bg-indigo-100/70 dark:group-hover:bg-indigo-950/50';
                      } else if (isInactive) {
                        rowBg = 'bg-red-200/80 hover:bg-red-200 dark:bg-red-900/30 dark:hover:bg-red-900/40';
                        stickyCellBg = 'bg-red-200/80 dark:bg-red-900/30 group-hover:bg-red-200 dark:group-hover:bg-red-900/40';
                      } else if (!hasFrames) {
                        rowBg = 'bg-amber-50/60 hover:bg-amber-100/60 dark:bg-amber-900/10 dark:hover:bg-amber-900/20';
                        stickyCellBg = 'bg-amber-50 dark:bg-amber-900/10 group-hover:bg-amber-100 dark:group-hover:bg-amber-900/20';
                      }

                      return (
                        <tr key={user.id} className={`transition-colors group ${rowBg}`}>

                          {/* User Info */}
                          <td className={`px-5 py-3.5 sticky left-0 z-10 transition-colors ${stickyCellBg}`}>
                            <div className="flex items-center gap-3">
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => handleSelectUser(user.id)}
                                className="w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer shrink-0"
                              />
                              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-indigo-400 to-purple-500 flex items-center justify-center text-white font-bold text-sm shrink-0">
                                {user.fullName.charAt(0).toUpperCase()}
                              </div>
                              <div>
                                <span className="block text-sm font-semibold text-slate-800 dark:text-slate-100">{user.fullName}</span>
                                <span className="block text-xs text-slate-500 dark:text-slate-400 mt-0.5">{user.country_code} {user.mobile_number}</span>
                              </div>
                            </div>
                          </td>

                          {/* Business Info */}
                          <td className="px-5 py-3.5">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-lg overflow-hidden border border-slate-200 dark:border-slate-700 bg-slate-100 dark:bg-slate-800 shrink-0">
                                <img src={user.businessLogo} className="w-full h-full object-cover" alt="Business Logo" />
                              </div>
                              <div>
                                <span className="block text-sm font-medium text-slate-700 dark:text-slate-200">{user.businessName || '—'}</span>
                                <span className="block text-xs text-slate-500 dark:text-slate-400 mt-0.5">{user.city || '—'}</span>
                              </div>
                            </div>
                          </td>

                          {/* Type */}
                          <td className="px-5 py-3.5">
                            <span className="block text-sm font-medium text-slate-700 dark:text-slate-200">{user.content_preference}</span>
                            <span className="block text-xs text-slate-500 dark:text-slate-400 mt-0.5">{user.business_type || '—'}</span>
                          </td>

                          {/* Plan */}
                          <td className="px-5 py-3.5">
                            {getStatusBadge(user.status, user.is_verified)}
                          </td>

                          {/* Group */}
                          <td className="px-5 py-3.5">
                            <span className="inline-flex px-2.5 py-1 rounded-full text-[11px] font-semibold bg-indigo-50 dark:bg-indigo-500/10 text-indigo-600 dark:text-indigo-400">
                              {user.user_group ? `Group ${user.user_group}` : 'None'}
                            </span>
                          </td>

                          {/* Join Date */}
                          <td className="px-5 py-3.5 text-sm text-slate-500 dark:text-slate-400 whitespace-nowrap">{user.joinDate}</td>

                          {/* Active Toggle */}
                          <td className="px-5 py-3.5 text-center">
                            <label className="relative inline-flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                className="sr-only peer"
                                checked={user.is_active === 1}
                                onChange={() => handleToggleActive(user.id, user.is_active)}
                              />
                              <div className="w-9 h-5 bg-slate-200 dark:bg-slate-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-indigo-500"></div>
                            </label>
                          </td>

                          {/* Actions */}
                          <td className={`px-5 py-3.5 text-right sticky right-0 transition-colors ${openDropdownId === user.id ? 'z-30 bg-white dark:bg-slate-900' : `z-10 ${stickyCellBg}`}`}>
                            <div className="relative inline-block text-left" ref={openDropdownId === user.id ? dropdownRef : null}>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setOpenDropdownId(openDropdownId === user.id ? null : user.id);
                                }}
                                className="p-1.5 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-500/10 rounded-lg transition-colors"
                              >
                                <MoreVertical size={18} />
                              </button>

                              {/* Dropdown Menu */}
                              {openDropdownId === user.id && (
                                <div className="absolute right-10 w-40 bg-white dark:bg-slate-800 rounded-xl shadow-xl border border-slate-100 dark:border-slate-700 overflow-hidden z-50 py-1 top-0 origin-top-right">
                                  <button
                                    className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700/50 hover:text-blue-600 transition-colors"
                                    onClick={() => { setOpenDropdownId(null); navigate('/users/' + user.id); }}
                                  >
                                    <Eye size={16} /> View Profile
                                  </button>

                                  <button
                                    className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700/50 hover:text-blue-600 transition-colors"
                                    onClick={() => {
                                      setOpenDropdownId(null);
                                      setFormData({
                                        mobile_number: user.mobile_number,
                                        country_code: user.country_code,
                                        fullName: user.fullName,
                                        email: user.email,
                                        designation: user.designation,
                                        date_of_birth: user.date_of_birth,
                                        gender: user.gender,
                                        city: user.city,
                                        business_name: user.business_name || user.businessName || "",
                                        businessName: user.business_name || user.businessName || "",
                                        business_email: user.business_email,
                                        business_phone: user.business_phone,
                                        business_address: user.business_address,
                                        business_description: user.business_description || "",
                                        business_type: user.business_type,
                                        content_preference: user.content_preference,
                                        status: user.status,
                                        user_group: user.user_group,
                                        businessLogo: user.businessLogo
                                      });
                                      setLogoFile(null);
                                      setLogoPreview(user.businessLogo || null);
                                      setEditingUserId(user.id);
                                      setCitySuggestions([]);
                                      setShowCitySuggestions(false);
                                      setIsModalOpen(true);
                                    }}
                                  >
                                    <Edit2 size={16} /> Edit
                                  </button>

                                  <div className="h-px bg-slate-100 dark:bg-slate-700 my-1"></div>
                                  <button
                                    className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors"
                                    onClick={() => handleDeleteUser(user.id)}
                                  >
                                    <Trash2 size={16} /> Delete
                                  </button>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination Controls Removed */}
          </div>
        </div>
      </div>

      {/* Add/Edit Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-2xl w-full max-w-4xl overflow-hidden flex flex-col max-h-[90vh]">
            <div className="px-6 py-4 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between bg-slate-50 dark:bg-slate-800/50 shrink-0">
              <div>
                <h3 className="font-bold text-lg text-slate-800 dark:text-slate-100">
                  {editingUserId ? "Edit User" : "Add New User"}
                </h3>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{editingUserId ? "Update user details" : "Fill in the details to create a new user"}</p>
              </div>
              <button
                onClick={() => setIsModalOpen(false)}
                className="text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 transition-colors p-2 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-700"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-6 space-y-5 overflow-y-auto">
              <div className="grid grid-cols-2 gap-5">
                <div className="col-span-2 sm:col-span-1">
                  <label className="block text-sm font-semibold text-slate-700 dark:text-slate-200 mb-1.5">Mobile Number <span className="text-red-500">*</span></label>
                  <div className="flex gap-2">
                    <input type="text"
                      value={formData.country_code}
                      onChange={e => setFormData({ ...formData, country_code: e.target.value })}
                      className="w-20 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 border border-slate-200 dark:border-slate-700 rounded-xl px-3 py-2.5 outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 transition-all text-sm" placeholder="+91" disabled={!!editingUserId} />
                    <input type="text"
                      value={formData.mobile_number}
                      onChange={e => setFormData({ ...formData, mobile_number: e.target.value })}
                      className="flex-1 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 border border-slate-200 dark:border-slate-700 rounded-xl px-4 py-2.5 outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 transition-all text-sm" placeholder="9876543210" disabled={!!editingUserId} />
                  </div>
                </div>
                <div className="col-span-2 sm:col-span-1">
                  <label className="block text-sm font-semibold text-slate-700 dark:text-slate-200 mb-1.5">Full Name </label>
                  <input type="text"
                    value={formData.fullName}
                    onChange={e => setFormData({ ...formData, fullName: e.target.value })}
                    className="w-full bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 border border-slate-200 dark:border-slate-700 rounded-xl px-4 py-2.5 outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 transition-all text-sm" placeholder="John Doe" />
                </div>
                <div className="col-span-2 sm:col-span-1">
                  <label className="block text-sm font-semibold text-slate-700 dark:text-slate-200 mb-1.5">Email Address</label>
                  <input type="email"
                    value={formData.email}
                    onChange={e => setFormData({ ...formData, email: e.target.value })}
                    className="w-full bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 border border-slate-200 dark:border-slate-700 rounded-xl px-4 py-2.5 outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 transition-all text-sm" placeholder="john@example.com" />
                </div>
                <div className="col-span-2 sm:col-span-1">
                  <label className="block text-sm font-semibold text-slate-700 dark:text-slate-200 mb-1.5">Date of Birth</label>


                  <input type="date"
                    value={formData.date_of_birth}
                    onChange={e => setFormData({ ...formData, date_of_birth: e.target.value })}
                    className="w-full bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 border border-slate-200 dark:border-slate-700 rounded-xl px-4 py-2.5 outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 transition-all text-sm" />
                </div>
                <div className="col-span-2 sm:col-span-1">
                  <label className="block text-sm font-semibold text-slate-700 dark:text-slate-200 mb-1.5">Gender</label>
                  <select
                    value={formData.gender}
                    onChange={e => setFormData({ ...formData, gender: e.target.value })}
                    className="w-full bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 border border-slate-200 dark:border-slate-700 rounded-xl px-4 py-2.5 outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 transition-all text-sm cursor-pointer">
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div className="col-span-2 sm:col-span-1">
                  <label className="block text-sm font-semibold text-slate-700 dark:text-slate-200 mb-1.5">Designation</label>
                  <input type="text"
                    value={formData.designation}
                    onChange={e => setFormData({ ...formData, designation: e.target.value })}
                    className="w-full bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 border border-slate-200 dark:border-slate-700 rounded-xl px-4 py-2.5 outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 transition-all text-sm" placeholder="Manager" />
                </div>
                <div className="col-span-2 sm:col-span-1 relative">
                  <label className="block text-sm font-semibold text-slate-700 dark:text-slate-200 mb-1.5 flex items-center justify-between">
                    <span className="flex items-center gap-1.5">
                      <MapPin size={15} className="text-blue-500" />
                      <span>City</span>
                    </span>
                    {formData.city && (
                      <span className="text-[11px] font-normal text-slate-400">
                        Type for suggestions
                      </span>
                    )}
                  </label>
                  <div className="relative">
                    <input
                      ref={cityInputRef}
                      type="text"
                      value={formData.city}
                      onChange={e => handleCityChange(e.target.value)}
                      onFocus={() => {
                        if (formData.city && formData.city.trim().length > 0) {
                          const matches = searchCities(formData.city, existingUserCities, 12);
                          setCitySuggestions(matches);
                          setShowCitySuggestions(matches.length > 0);
                        }
                      }}
                      onKeyDown={handleCityKeyDown}
                      className="w-full bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 border border-slate-200 dark:border-slate-700 rounded-xl pl-9 pr-8 py-2.5 outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 transition-all text-sm"
                      placeholder="e.g. Indore, Bhopal, Mumbai..."
                      autoComplete="off"
                    />
                    <MapPin size={16} className="absolute left-3 top-3 text-slate-400 pointer-events-none" />
                    {formData.city && (
                      <button
                        type="button"
                        onClick={() => {
                          setFormData(prev => ({ ...prev, city: "" }));
                          setCitySuggestions([]);
                          setShowCitySuggestions(false);
                        }}
                        className="absolute right-2.5 top-2.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 p-0.5 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                        title="Clear City"
                      >
                        <X size={14} />
                      </button>
                    )}
                  </div>

                  {/* Autocomplete City Suggestions Dropdown */}
                  {showCitySuggestions && citySuggestions.length > 0 && (
                    <div
                      ref={cityDropdownRef}
                      className="absolute left-0 right-0 top-full mt-1.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-xl max-h-60 overflow-y-auto z-50 divide-y divide-slate-100 dark:divide-slate-700/60 animate-in fade-in slide-in-from-top-1 duration-150"
                    >
                      <div className="px-3 py-1.5 bg-slate-50 dark:bg-slate-900/60 text-[11px] font-semibold text-slate-500 dark:text-slate-400 flex items-center justify-between uppercase tracking-wider sticky top-0 backdrop-blur-sm z-10">
                        <span>Suggested Cities</span>
                        <span>{citySuggestions.length} found</span>
                      </div>
                      {citySuggestions.map((cityName, idx) => {
                        const isSelected = activeCityIndex === idx;
                        const q = (formData.city || "").trim();
                        const lowerCity = cityName.toLowerCase();
                        const matchIndex = lowerCity.indexOf(q.toLowerCase());

                        return (
                          <div
                            key={cityName + idx}
                            onClick={() => handleSelectCity(cityName)}
                            onMouseEnter={() => setActiveCityIndex(idx)}
                            className={`px-3.5 py-2.5 cursor-pointer text-sm flex items-center justify-between transition-colors ${
                              isSelected
                                ? "bg-blue-50 dark:bg-blue-950/60 text-blue-600 dark:text-blue-400 font-medium"
                                : "text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700/50"
                            }`}
                          >
                            <div className="flex items-center gap-2">
                              <MapPin size={14} className={isSelected ? "text-blue-500" : "text-slate-400"} />
                              <span>
                                {matchIndex >= 0 ? (
                                  <>
                                    {cityName.slice(0, matchIndex)}
                                    <strong className="text-blue-600 dark:text-blue-400 underline decoration-blue-300">
                                      {cityName.slice(matchIndex, matchIndex + q.length)}
                                    </strong>
                                    {cityName.slice(matchIndex + q.length)}
                                  </>
                                ) : (
                                  cityName
                                )}
                              </span>
                            </div>
                            {POPULAR_CITIES.has(cityName) && (
                              <span className="text-[10px] bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 px-1.5 py-0.5 rounded font-medium">
                                Popular
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
                <div className="col-span-2 sm:col-span-1">
                  <label className="block text-sm font-semibold text-slate-700 dark:text-slate-200 mb-1.5">Content Preference</label>
                  <select
                    value={formData.content_preference}
                    onChange={e => setFormData({ ...formData, content_preference: e.target.value, business_type: "" })}
                    className="w-full bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 border border-slate-200 dark:border-slate-700 rounded-xl px-4 py-2.5 outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 transition-all text-sm cursor-pointer">
                    <option value="Business">Business</option>
                    <option value="Politics">Politics</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                {(formData.content_preference === 'Business' || formData.content_preference === 'Politics') && (
                  <>
                    <div className="col-span-2 mt-2">
                      <h4 className="font-semibold text-slate-800 dark:text-slate-100 border-b border-slate-100 dark:border-slate-700 pb-2">
                        {formData.content_preference === 'Politics' ? 'Political Details' : 'Business Details'}
                      </h4>
                    </div>

                    <div className="col-span-2 sm:col-span-1">
                      <label className="block text-sm font-semibold text-slate-700 dark:text-slate-200 mb-1.5">
                        Business Name
                      </label>
                      <input
                        type="text"
                        value={formData.business_name || formData.businessName || ""}
                        onChange={e => setFormData({ ...formData, business_name: e.target.value, businessName: e.target.value })}
                        className="w-full bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 border border-slate-200 dark:border-slate-700 rounded-xl px-4 py-2.5 outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 transition-all text-sm"
                        placeholder="Business Name"
                      />
                    </div>

                    <div className="col-span-2 sm:col-span-1">
                      <label className="block text-sm font-semibold text-slate-700 dark:text-slate-200 mb-1.5">
                        {formData.content_preference === 'Politics' ? 'Party Name' : 'Business Type'}
                      </label>
                      <select
                        value={formData.business_type || ""}
                        onChange={e => setFormData({ ...formData, business_type: e.target.value })}
                        className="w-full bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 border border-slate-200 dark:border-slate-700 rounded-xl px-4 py-2.5 outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 transition-all text-sm cursor-pointer"
                      >
                        <option value="">{formData.content_preference === 'Politics' ? 'Select Party' : 'Select Type'}</option>
                        {preferenceOptions.map((opt, i) => (
                          <option key={i} value={opt.name || opt.business_name}>{opt.name || opt.business_name}</option>
                        ))}
                      </select>
                    </div>
                    <div className="col-span-2 sm:col-span-1">
                      <label className="block text-sm font-semibold text-slate-700 dark:text-slate-200 mb-1.5">
                        {formData.content_preference === 'Politics' ? 'Email' : 'Business Email'}
                      </label>
                      <input type="email"
                        value={formData.business_email}
                        onChange={e => setFormData({ ...formData, business_email: e.target.value })}
                        className="w-full bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 border border-slate-200 dark:border-slate-700 rounded-xl px-4 py-2.5 outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 transition-all text-sm" placeholder="info@example.com" />
                    </div>
                    <div className="col-span-2 sm:col-span-1">
                      <label className="block text-sm font-semibold text-slate-700 dark:text-slate-200 mb-1.5">
                        {formData.content_preference === 'Politics' ? 'Phone' : 'Business Phone'}
                      </label>
                      <input type="text"
                        value={formData.business_phone}
                        onChange={e => setFormData({ ...formData, business_phone: e.target.value })}
                        className="w-full bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 border border-slate-200 dark:border-slate-700 rounded-xl px-4 py-2.5 outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 transition-all text-sm" placeholder="Phone" />
                    </div>
                    <div className="col-span-2">
                      <label className="block text-sm font-semibold text-slate-700 dark:text-slate-200 mb-1.5">
                        {formData.content_preference === 'Politics' ? 'Address' : 'Business Address'}
                      </label>
                      <textarea
                        value={formData.business_address}
                        onChange={e => setFormData({ ...formData, business_address: e.target.value })}
                        className="w-full bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 border border-slate-200 dark:border-slate-700 rounded-xl px-4 py-2.5 outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 transition-all text-sm" placeholder="Full Address..." rows="2" />
                    </div>
                    <div className="col-span-2">
                      <label className="block text-sm font-semibold text-slate-700 dark:text-slate-200 mb-1.5">
                        {formData.content_preference === 'Politics' ? 'Description' : 'Business Description'}
                      </label>
                      <textarea
                        value={formData.business_description}
                        onChange={e => setFormData({ ...formData, business_description: e.target.value })}
                        className="w-full bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 border border-slate-200 dark:border-slate-700 rounded-xl px-4 py-2.5 outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 transition-all text-sm" placeholder="Brief description..." rows="2" />
                    </div>

                    <div className="col-span-2">
                      <label className="block text-sm font-semibold text-slate-700 dark:text-slate-200 mb-1.5">
                        {formData.content_preference === 'Politics' ? 'Party / Business Logo (Auto Background Removal)' : 'Business Logo (Auto Background Removal)'}
                      </label>
                      <div className="flex items-center gap-4 bg-slate-50 dark:bg-slate-900/50 p-3 rounded-xl border border-slate-200 dark:border-slate-700">
                        <div className="w-16 h-16 rounded-xl border border-slate-200 dark:border-slate-700 bg-[radial-gradient(#cbd5e1_1px,transparent_1px)] [background-size:6px_6px] bg-slate-100 dark:bg-slate-800 flex items-center justify-center overflow-hidden shrink-0 shadow-inner">
                          {logoPreview ? (
                            <img src={logoPreview} alt="Logo Preview" className="max-w-full max-h-full object-contain" />
                          ) : (
                            <span className="text-[10px] text-slate-400 font-medium">No Logo</span>
                          )}
                        </div>
                        <div className="flex-1">
                          <label className="cursor-pointer inline-flex items-center gap-1.5 px-3.5 py-2 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-xl text-xs font-semibold hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors shadow-sm">
                            <Upload size={14} /> {logoPreview ? "Change Logo" : "Upload Logo"}
                            <input
                              type="file"
                              accept="image/png, image/jpeg, image/jpg, image/webp"
                              className="hidden"
                              onChange={(e) => {
                                const f = e.target.files && e.target.files[0];
                                if (f) {
                                  setLogoFile(f);
                                  setLogoPreview(URL.createObjectURL(f));
                                }
                              }}
                            />
                          </label>
                          <p className="text-xs text-emerald-600 dark:text-emerald-400 font-medium mt-1.5 flex items-center gap-1">
                            <span>✓</span> White & solid background will be automatically removed
                          </p>
                          {logoFile && (
                            <p className="text-[11px] text-slate-500 dark:text-slate-400 truncate mt-0.5">
                              Selected: {logoFile.name}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  </>
                )}

                <div className="col-span-2 border-t border-slate-100 dark:border-slate-700 pt-4 mt-2">
                  <label className="block text-sm font-semibold text-slate-700 dark:text-slate-200 mb-1.5">Account Status & Plan</label>
                  <select
                    value={formData.status}
                    onChange={e => setFormData({ ...formData, status: e.target.value })}
                    className="w-full border border-slate-200 dark:border-slate-700 rounded-xl px-4 py-2.5 outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 transition-all text-sm bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 cursor-pointer">
                    <option value="Free">Free </option>
                    <option value="Premium">Premium (is_verified = 0)</option>
                    <option value="Blocked">Blocked</option>
                  </select>
                </div>

                <div className="col-span-2 border-t border-slate-100 dark:border-slate-700 pt-4 mt-2">
                  <label className="block text-sm font-semibold text-slate-700 dark:text-slate-200 mb-1.5">User Group</label>
                  <select
                    value={formData.user_group}
                    onChange={e => setFormData({ ...formData, user_group: e.target.value })}
                    className="w-full border border-slate-200 dark:border-slate-700 rounded-xl px-4 py-2.5 outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 transition-all text-sm bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 cursor-pointer">
                    <option value="">None</option>
                    {userGroups.map((grp) => (
                      <option key={grp.group_id_PK} value={grp.group_name}>{grp.group_name}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <div className="px-6 py-4 border-t border-slate-100 dark:border-slate-700 flex items-center justify-end gap-3 bg-slate-50/50 dark:bg-slate-800/50 dark:bg-slate-900/50">
              <button
                onClick={() => setIsModalOpen(false)}
                className="px-4 py-2.5 text-sm font-semibold text-slate-600 hover:text-slate-800 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-xl transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveUser}
                className="px-6 py-2.5 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 shadow-sm hover:shadow-md rounded-xl transition-all"
              >
                {editingUserId ? "Update User" : "Create User"}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
